import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { toast } from 'react-toastify';
import { errorHandler } from '@/utils/functions/errorHandler';
import useCompanyState from './company.state';
import useCompanyProfileCompletionState from './completion.state';

export interface IReward {
  id?: number | string;
  tenant_id?: number | string;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  name?: string;
  value?: string;
  description?: string;
  is_published?: boolean;
  attachment_file?: any;
  created_at?: string;
  updated_at?: string;
  fileUrl?: string;
}

interface RewardState {
  rewards: IReward[];
  selectedReward: IReward | null;
  isLoading: boolean;
  error: unknown;

  createReward: (tenantId: string | number, payload: Partial<IReward>) => Promise<void>;
  getRewards: (tenantId: string | number) => Promise<void>;
  getRewardDetail: (tenantId: string | number, id: string | number) => Promise<IReward | void>;
  updateReward: (tenantId: string | number, id: string | number, payload: Partial<IReward>) => Promise<void>;
  deleteReward: (tenantId: string | number, id: string | number) => Promise<void>;
  clearRewards: () => void;
  setRewards: (rewards: IReward[]) => void;
  bulkPublishReward: (tenantId: string | number, isPublished: boolean) => Promise<void>;
}

const useRewardState = create<RewardState>((set, get) => ({
  rewards: [],
  selectedReward: null,
  isLoading: false,
  error: null,

  setRewards: rewards => set({ rewards }),
  clearRewards: () => set({ rewards: [] }),

  createReward: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const { uploadCompanyMedia } = useCompanyState.getState();

      if (payload.attachment_file) {
        const file = Array.isArray(payload.attachment_file) ? payload.attachment_file[0] : payload.attachment_file;

        const uploadRes = await uploadCompanyMedia(file, tenantId, 'Reward Attachment');
        const uploadedFile = uploadRes?.data?.[0] || uploadRes?.[0] || uploadRes;

        payload.attachment_file = JSON.stringify([
          {
            disk: uploadedFile.disk || 'minio',
            path: uploadedFile.path,
            filename: uploadedFile.filename,
            original_name: uploadedFile.original_name,
            mime_type: uploadedFile.mime_type,
            size: uploadedFile.size,
            extension: uploadedFile.extension,
            url: uploadedFile.url,
            uploaded_at: uploadedFile.uploaded_at || new Date().toISOString(),
            content_type: uploadedFile.content_type || uploadedFile.mime_type,
          },
        ]);
      }

      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}`;
      await axiosInstance.post(url, payload);

      await get().getRewards(tenantId);      
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('Reward created successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  updateReward: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const { uploadCompanyMedia } = useCompanyState.getState();

      const attachment = payload.attachment_file;
      let firstFile: unknown;
      if (Array.isArray(attachment)) {
        firstFile = attachment[0];
      } else if (attachment instanceof File) {
        firstFile = attachment;
      } else {
        firstFile = (attachment as any)?.file || (attachment as any)?.originFileObj;
      }

      let actualFile: File | null;
      if (firstFile instanceof File) {
        actualFile = firstFile;
      } else if ((firstFile as any)?.originFileObj instanceof File) {
        actualFile = (firstFile as any).originFileObj;
      } else if ((firstFile as any)?.file instanceof File) {
        actualFile = (firstFile as any).file;
      } else {
        actualFile = null;
      }

      const hasNewFile = !!actualFile;

      if (hasNewFile) {
        const uploadRes = await uploadCompanyMedia(actualFile!, tenantId, 'Reward Attachment');
        const uploadedFile = uploadRes?.data?.[0] || uploadRes?.[0] || uploadRes;

        payload.attachment_file = JSON.stringify([
          {
            disk: uploadedFile.disk || 'minio',
            path: uploadedFile.path,
            filename: uploadedFile.filename,
            original_name: uploadedFile.original_name,
            mime_type: uploadedFile.mime_type,
            size: uploadedFile.size,
            extension: uploadedFile.extension,
            url: uploadedFile.url,
            uploaded_at: uploadedFile.uploaded_at || new Date().toISOString(),
            content_type: uploadedFile.content_type || uploadedFile.mime_type,
          },
        ]);
      } else {
        delete payload.attachment_file;
      }

      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, payload);

      set(state => ({
        rewards: state.rewards.map(rw => (rw.id === id ? { ...rw, ...data.data } : rw)),
        selectedReward: data.data,
      }));

      toast.success('Reward updated successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getRewards: async tenantId => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}`;
      const { data } = await axiosInstance.get(url);

      const rewardsData = (data.data?.content || data.data || []).map((reward: IReward) => {
        let fileUrl: string | null = null;
        if (reward.attachment_file) {
          try {
            const files = JSON.parse(reward.attachment_file);
            if (files.length > 0 && files[0].url) {
              fileUrl = `${axiosInstance.defaults.cloudStorage || 'https://minio.merapi.javan.id'}${files[0].url}`;
            }
          } catch {
            fileUrl = null;
          }
        }
        return { ...reward, fileUrl };
      });

      set({ rewards: rewardsData });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  getRewardDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      set({ selectedReward: data.data });
      return data.data;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  deleteReward: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}/${id}`;
      await axiosInstance.delete(url);
      set(state => ({
        rewards: state.rewards.filter(rw => rw.id !== id),
      }));
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('Reward deleted successfully');
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  bulkPublishReward: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/rewards/${tenantId}/bulk-publish`;
      await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        rewards: state.rewards.map(ka => ({
          ...ka,
          published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(isPublished ? 'All Rewards published successfully' : 'All Rewards unpublished successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default useRewardState;
